declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

export type ClarityMetadata = {
  userId: string | null;
  sessionId: string | null;
  projectId: string | null;
};

let cache: ClarityMetadata = {
  userId: null,
  sessionId: null,
  projectId: null,
};

/**
 * Clarity's `metadata` callback is async — we can never read its values from
 * the same tick. So we keep a module-level cache, refresh it in the background
 * on every read, and let downstream consumers stay synchronous.
 */
function refresh(): void {
  if (typeof window === "undefined") return;
  const c = window.clarity;
  if (typeof c !== "function") return;
  try {
    c(
      "metadata",
      (md:
        | { userId?: string; sessionId?: string; projectId?: string }
        | undefined) => {
        if (!md) return;
        cache = {
          userId: md.userId ?? null,
          sessionId: md.sessionId ?? null,
          projectId: md.projectId ?? null,
        };
      },
    );
  } catch {
    /* ignore */
  }
}

/** Call once `window.clarity` is available so the cache is warm. */
export function primeClarityMetadata(): void {
  refresh();
}

/**
 * Synchronous snapshot of the last known Clarity metadata. Triggers a
 * background refresh so subsequent reads stay fresh across SPA navigations
 * and after Clarity rotates the sessionId (~30 min idle).
 */
export function getClarityMetadataSnapshot(): ClarityMetadata {
  refresh();
  return cache;
}
